// Curated library of REAL, peer-reviewed medical citations used across the
// TruWe Clinical Library. Every entry is a landmark, PubMed-verifiable paper.
// Format: Author(s). Title. Journal. Year;Volume(Issue):Pages.

export type Reference = {
  id: string;
  authors: string;
  title: string;
  journal: string;
  year: number;
  citation: string; // Full formatted string
  url: string; // PubMed / DOI link
};

export const REFERENCES: Record<string, Reference> = {
  abbasi_2012: {
    id: "abbasi_2012",
    authors: "Abbasi B, Kimiagar M, Sadeghniiat K, Shirazi MM, Hedayati M, Rashidkhani B",
    title: "The effect of magnesium supplementation on primary insomnia in elderly: A double-blind placebo-controlled clinical trial",
    journal: "Journal of Research in Medical Sciences",
    year: 2012,
    citation: "Abbasi B, Kimiagar M, Sadeghniiat K, et al. The effect of magnesium supplementation on primary insomnia in elderly: A double-blind placebo-controlled clinical trial. J Res Med Sci. 2012;17(12):1161-1169.",
    url: "https://pubmed.ncbi.nlm.nih.gov/23853635/",
  },
  boyle_2017: {
    id: "boyle_2017",
    authors: "Boyle NB, Lawton C, Dye L",
    title: "The effects of magnesium supplementation on subjective anxiety and stress, a systematic review",
    journal: "Nutrients",
    year: 2017,
    citation: "Boyle NB, Lawton C, Dye L. The effects of magnesium supplementation on subjective anxiety and stress, a systematic review. Nutrients. 2017;9(5):429.",
    url: "https://pubmed.ncbi.nlm.nih.gov/28445426/",
  },
  rosanoff_2012: {
    id: "rosanoff_2012",
    authors: "Rosanoff A, Weaver CM, Rude RK",
    title: "Suboptimal magnesium status in the United States: are the health consequences underestimated?",
    journal: "Nutrition Reviews",
    year: 2012,
    citation: "Rosanoff A, Weaver CM, Rude RK. Suboptimal magnesium status in the United States: are the health consequences underestimated? Nutr Rev. 2012;70(3):153-164.",
    url: "https://pubmed.ncbi.nlm.nih.gov/22364157/",
  },
  garrison_2020: {
    id: "garrison_2020",
    authors: "Garrison SR, Korownyk CS, Kolber MR, Allan GM, Musini VM, Sekhon RK, Dugré N",
    title: "Magnesium for skeletal muscle cramps",
    journal: "Cochrane Database of Systematic Reviews",
    year: 2020,
    citation: "Garrison SR, Korownyk CS, Kolber MR, et al. Magnesium for skeletal muscle cramps. Cochrane Database Syst Rev. 2020;9(9):CD009402.",
    url: "https://pubmed.ncbi.nlm.nih.gov/32956536/",
  },
  debaaij_2015: {
    id: "debaaij_2015",
    authors: "de Baaij JH, Hoenderop JG, Bindels RJ",
    title: "Magnesium in man: implications for health and disease",
    journal: "Physiological Reviews",
    year: 2015,
    citation: "de Baaij JH, Hoenderop JG, Bindels RJ. Magnesium in man: implications for health and disease. Physiol Rev. 2015;95(1):1-46.",
    url: "https://pubmed.ncbi.nlm.nih.gov/25540137/",
  },
  barbagallo_2015: {
    id: "barbagallo_2015",
    authors: "Barbagallo M, Dominguez LJ",
    title: "Magnesium and type 2 diabetes",
    journal: "World Journal of Diabetes",
    year: 2015,
    citation: "Barbagallo M, Dominguez LJ. Magnesium and type 2 diabetes. World J Diabetes. 2015;6(10):1152-1157.",
    url: "https://pubmed.ncbi.nlm.nih.gov/26322160/",
  },
  dinicolantonio_2018: {
    id: "dinicolantonio_2018",
    authors: "DiNicolantonio JJ, O'Keefe JH, Wilson W",
    title: "Subclinical magnesium deficiency: a principal driver of cardiovascular disease and a public health crisis",
    journal: "Open Heart",
    year: 2018,
    citation: "DiNicolantonio JJ, O'Keefe JH, Wilson W. Subclinical magnesium deficiency: a principal driver of cardiovascular disease and a public health crisis. Open Heart. 2018;5(1):e000668.",
    url: "https://pubmed.ncbi.nlm.nih.gov/29387426/",
  },
  shoba_1998: {
    id: "shoba_1998",
    authors: "Shoba G, Joy D, Joseph T, Majeed M, Rajendran R, Srinivas PS",
    title: "Influence of piperine on the pharmacokinetics of curcumin in animals and human volunteers",
    journal: "Planta Medica",
    year: 1998,
    citation: "Shoba G, Joy D, Joseph T, et al. Influence of piperine on the pharmacokinetics of curcumin in animals and human volunteers. Planta Med. 1998;64(4):353-356.",
    url: "https://pubmed.ncbi.nlm.nih.gov/9619120/",
  },
  cuomo_2011: {
    id: "cuomo_2011",
    authors: "Cuomo J, Appendino G, Dern AS, Schneider E, McKinnon TP, Brown MJ, Togni S, Dixon BM",
    title: "Comparative absorption of a standardized curcuminoid mixture and its lecithin formulation",
    journal: "Journal of Natural Products",
    year: 2011,
    citation: "Cuomo J, Appendino G, Dern AS, et al. Comparative absorption of a standardized curcuminoid mixture and its lecithin formulation. J Nat Prod. 2011;74(4):664-669.",
    url: "https://pubmed.ncbi.nlm.nih.gov/21413691/",
  },
  hewlings_2017: {
    id: "hewlings_2017",
    authors: "Hewlings SJ, Kalman DS",
    title: "Curcumin: a review of its effects on human health",
    journal: "Foods",
    year: 2017,
    citation: "Hewlings SJ, Kalman DS. Curcumin: a review of its effects on human health. Foods. 2017;6(10):92.",
    url: "https://pubmed.ncbi.nlm.nih.gov/29065496/",
  },
  yin_2008: {
    id: "yin_2008",
    authors: "Yin J, Xing H, Ye J",
    title: "Efficacy of berberine in patients with type 2 diabetes mellitus",
    journal: "Metabolism",
    year: 2008,
    citation: "Yin J, Xing H, Ye J. Efficacy of berberine in patients with type 2 diabetes mellitus. Metabolism. 2008;57(5):712-717.",
    url: "https://pubmed.ncbi.nlm.nih.gov/18442638/",
  },
  dong_2012: {
    id: "dong_2012",
    authors: "Dong H, Wang N, Zhao L, Lu F",
    title: "Berberine in the treatment of type 2 diabetes mellitus: a systematic review and meta-analysis",
    journal: "Evidence-Based Complementary and Alternative Medicine",
    year: 2012,
    citation: "Dong H, Wang N, Zhao L, Lu F. Berberine in the treatment of type 2 diabetes mellitus: a systematic review and meta-analysis. Evid Based Complement Alternat Med. 2012;2012:591654.",
    url: "https://pubmed.ncbi.nlm.nih.gov/23118793/",
  },
  lan_2015: {
    id: "lan_2015",
    authors: "Lan J, Zhao Y, Dong F, Yan Z, Zheng W, Fan J, Sun G",
    title: "Meta-analysis of the effect and safety of berberine in the treatment of type 2 diabetes mellitus, hyperlipemia and hypertension",
    journal: "Journal of Ethnopharmacology",
    year: 2015,
    citation: "Lan J, Zhao Y, Dong F, et al. Meta-analysis of the effect and safety of berberine in the treatment of type 2 diabetes mellitus, hyperlipemia and hypertension. J Ethnopharmacol. 2015;161:69-81.",
    url: "https://pubmed.ncbi.nlm.nih.gov/25498346/",
  },
  unfer_2017: {
    id: "unfer_2017",
    authors: "Unfer V, Facchinetti F, Orrù B, Giordani B, Nestler J",
    title: "Myo-inositol effects in women with PCOS: a meta-analysis of randomized controlled trials",
    journal: "Endocrine Connections",
    year: 2017,
    citation: "Unfer V, Facchinetti F, Orrù B, Giordani B, Nestler J. Myo-inositol effects in women with PCOS: a meta-analysis of randomized controlled trials. Endocr Connect. 2017;6(8):647-658.",
    url: "https://pubmed.ncbi.nlm.nih.gov/29042448/",
  },
  nordio_2012: {
    id: "nordio_2012",
    authors: "Nordio M, Proietti E",
    title: "The combined therapy with myo-inositol and D-chiro-inositol reduces the risk of metabolic disease in PCOS overweight patients compared to myo-inositol supplementation alone",
    journal: "European Review for Medical and Pharmacological Sciences",
    year: 2012,
    citation: "Nordio M, Proietti E. The combined therapy with myo-inositol and D-chiro-inositol reduces the risk of metabolic disease in PCOS overweight patients compared to myo-inositol supplementation alone. Eur Rev Med Pharmacol Sci. 2012;16(5):575-581.",
    url: "https://pubmed.ncbi.nlm.nih.gov/22774396/",
  },
  facchinetti_2015: {
    id: "facchinetti_2015",
    authors: "Facchinetti F, Bizzarri M, Benvenga S, D'Anna R, Lanzone A, Soulage C, et al.",
    title: "Results from the International Consensus Conference on myo-inositol and d-chiro-inositol in obstetrics and gynecology",
    journal: "European Journal of Obstetrics & Gynecology and Reproductive Biology",
    year: 2015,
    citation: "Facchinetti F, Bizzarri M, Benvenga S, et al. Results from the International Consensus Conference on myo-inositol and d-chiro-inositol in obstetrics and gynecology. Eur J Obstet Gynecol Reprod Biol. 2015;195:72-76.",
    url: "https://pubmed.ncbi.nlm.nih.gov/26479442/",
  },
  vilargomez_2015: {
    id: "vilargomez_2015",
    authors: "Vilar-Gomez E, Martinez-Perez Y, Calzadilla-Bertot L, Torres-Gonzalez A, Gra-Oramas B, Gonzalez-Fabian L, et al.",
    title: "Weight loss through lifestyle modification significantly reduces features of nonalcoholic steatohepatitis",
    journal: "Gastroenterology",
    year: 2015,
    citation: "Vilar-Gomez E, Martinez-Perez Y, Calzadilla-Bertot L, et al. Weight loss through lifestyle modification significantly reduces features of nonalcoholic steatohepatitis. Gastroenterology. 2015;149(2):367-378.e5.",
    url: "https://pubmed.ncbi.nlm.nih.gov/25865049/",
  },
  younossi_2016: {
    id: "younossi_2016",
    authors: "Younossi ZM, Koenig AB, Abdelatif D, Fazel Y, Henry L, Wymer M",
    title: "Global epidemiology of nonalcoholic fatty liver disease, meta-analytic assessment of prevalence, incidence, and outcomes",
    journal: "Hepatology",
    year: 2016,
    citation: "Younossi ZM, Koenig AB, Abdelatif D, et al. Global epidemiology of nonalcoholic fatty liver disease, meta-analytic assessment of prevalence, incidence, and outcomes. Hepatology. 2016;64(1):73-84.",
    url: "https://pubmed.ncbi.nlm.nih.gov/26707365/",
  },
  abenavoli_2018: {
    id: "abenavoli_2018",
    authors: "Abenavoli L, Izzo AA, Milić N, Cicala C, Santini A, Capasso R",
    title: "Milk thistle (Silybum marianum): a concise overview on its chemistry, pharmacological, and nutraceutical uses in liver diseases",
    journal: "Phytotherapy Research",
    year: 2018,
    citation: "Abenavoli L, Izzo AA, Milić N, et al. Milk thistle (Silybum marianum): a concise overview on its chemistry, pharmacological, and nutraceutical uses in liver diseases. Phytother Res. 2018;32(11):2202-2213.",
    url: "https://pubmed.ncbi.nlm.nih.gov/30080294/",
  },
  tilg_2022: {
    id: "tilg_2022",
    authors: "Tilg H, Adolph TE, Trauner M",
    title: "Gut-liver axis: pathophysiological concepts and clinical implications",
    journal: "Cell Metabolism",
    year: 2022,
    citation: "Tilg H, Adolph TE, Trauner M. Gut-liver axis: pathophysiological concepts and clinical implications. Cell Metab. 2022;34(11):1700-1718.",
    url: "https://pubmed.ncbi.nlm.nih.gov/36208625/",
  },
  wieland_2015: {
    id: "wieland_2015",
    authors: "Wieland A, Frank DN, Harnke B, Bambha K",
    title: "Systematic review: microbial dysbiosis and nonalcoholic fatty liver disease",
    journal: "Alimentary Pharmacology & Therapeutics",
    year: 2015,
    citation: "Wieland A, Frank DN, Harnke B, Bambha K. Systematic review: microbial dysbiosis and nonalcoholic fatty liver disease. Aliment Pharmacol Ther. 2015;42(9):1051-1063.",
    url: "https://pubmed.ncbi.nlm.nih.gov/26304302/",
  },
  ford_2014: {
    id: "ford_2014",
    authors: "Ford AC, Quigley EM, Lacy BE, Lembo AJ, Saito YA, Schiller LR, et al.",
    title: "Efficacy of prebiotics, probiotics, and synbiotics in irritable bowel syndrome and chronic idiopathic constipation: systematic review and meta-analysis",
    journal: "American Journal of Gastroenterology",
    year: 2014,
    citation: "Ford AC, Quigley EM, Lacy BE, et al. Efficacy of prebiotics, probiotics, and synbiotics in irritable bowel syndrome and chronic idiopathic constipation: systematic review and meta-analysis. Am J Gastroenterol. 2014;109(10):1547-1561.",
    url: "https://pubmed.ncbi.nlm.nih.gov/25070051/",
  },
};

// Per-article citation whitelists (topic-matched).
export const ARTICLE_REFERENCES: Record<string, string[]> = {
  "best-magnesium-supplement-for-sleep": ["abbasi_2012", "boyle_2017", "debaaij_2015", "rosanoff_2012"],
  "magnesium-benefits": ["debaaij_2015", "rosanoff_2012", "barbagallo_2015", "dinicolantonio_2018"],
  "magnesium-glycinate-vs-citrate-india": ["debaaij_2015", "rosanoff_2012", "garrison_2020"],
  "magnesium-deficiency-symptoms-india": ["rosanoff_2012", "dinicolantonio_2018", "debaaij_2015", "barbagallo_2015"],
  "magnesium-rich-foods-india": ["rosanoff_2012", "debaaij_2015", "dinicolantonio_2018"],
  "leg-cramps-at-night-causes-india": ["garrison_2020", "debaaij_2015", "rosanoff_2012"],
  "natural-remedies-sleep-anxiety-india": ["boyle_2017", "abbasi_2012", "debaaij_2015"],
  "curcumin-bioavailability": ["shoba_1998", "cuomo_2011", "hewlings_2017"],
  "berberine-guide": ["yin_2008", "dong_2012", "lan_2015"],
  "inositol-vs-berberine-insulin-resistance-pmos": ["unfer_2017", "facchinetti_2015", "yin_2008", "lan_2015"],
  "berberine-vs-metformin-india": ["yin_2008", "dong_2012", "lan_2015"],
  "inositol-pcos": ["unfer_2017", "nordio_2012", "facchinetti_2015"],
  "pcos-inositol-guide-india": ["unfer_2017", "nordio_2012", "facchinetti_2015"],
  "liver-pcos-connection-india": [
    "unfer_2017",
    "facchinetti_2015",
    "lan_2015",
    "younossi_2016",
    "abenavoli_2018",
    "tilg_2022",
  ],
  "irregular-periods-pcos-checklist": ["unfer_2017", "facchinetti_2015"],
  "why-is-my-period-late": ["unfer_2017", "facchinetti_2015", "nordio_2012"],
  "liver-detox-truth": ["vilargomez_2015", "younossi_2016", "abenavoli_2018"],
  "sluggish-liver-signs-and-fix": ["vilargomez_2015", "abenavoli_2018", "tilg_2022"],
  "fatty-liver-diet-plan-india": ["vilargomez_2015", "younossi_2016", "wieland_2015"],
  "gut-liver-axis": ["tilg_2022", "wieland_2015", "abenavoli_2018"],
  "bloating-after-meals-causes-india": ["ford_2014", "wieland_2015"],
};

export function getReferences(slug: string): Reference[] {
  const ids = ARTICLE_REFERENCES[slug] ?? [];
  return ids.map((id) => REFERENCES[id]).filter(Boolean);
}
